import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useBack } from '../../hooks/useBack';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client';
import { unwrapArray } from '../../lib/api-envelope';
import { PREFILL_LABELS, fieldPrefill, prefillSourcesFor, type PrefillSource } from '../../lib/safety-form-prefill';
import {
  FormLibraryTemplate,
  buildCategoryOptions,
  listFormCategories,
} from '../../lib/form-library';
import {
  ArrowLeft, Plus, Trash2, ChevronUp, ChevronDown, GripVertical, Copy,
  Type, AlignLeft, Hash, ToggleLeft, CheckSquare, List, Calendar,
  Clock, Camera, PenTool, MapPin, Users, FileText, Loader2, AlertTriangle,
  CircleDot, GitBranch,
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  CHOICE_FIELD_TYPES,
  INLINE_LAYOUT_TYPES,
  RULE_PARENT_TYPES,
  FormChoice,
  NormalizedChoice,
  ShowIf,
  dependentsOf,
  newFieldId,
  normalizeChoices,
  ruleValues,
  ruleValuesFor,
  serializeChoices,
} from '../../lib/safety-form-logic';

const FIELD_TYPES = [
  { value: 'section_header', label: 'Section Header', icon: FileText },
  { value: 'instructions', label: 'Instructions', icon: AlignLeft },
  { value: 'text', label: 'Text Input', icon: Type },
  { value: 'textarea', label: 'Multi-line Text', icon: AlignLeft },
  { value: 'number', label: 'Number', icon: Hash },
  { value: 'yes_no', label: 'Yes / No', icon: ToggleLeft },
  { value: 'checkbox_group', label: 'Checkbox Group', icon: CheckSquare },
  { value: 'radio', label: 'Radio (pick one)', icon: CircleDot },
  { value: 'dropdown', label: 'Dropdown', icon: List },
  { value: 'date', label: 'Date', icon: Calendar },
  { value: 'time', label: 'Time', icon: Clock },
  { value: 'photo', label: 'Photo', icon: Camera },
  { value: 'signature', label: 'Signature', icon: PenTool },
  { value: 'gps_location', label: 'GPS Location', icon: MapPin },
  { value: 'worker_select', label: 'Worker Select', icon: Users },
] as const;

// Categories are NOT hardcoded here. The list that used to live on this line
// offered `inspection` and `other`, which match nothing on any live tenant,
// and omitted `general` and `incident` — so a template stored as `general`
// found no matching <option> and the select displayed "Jsa" instead. They come
// from the same live source the library page groups by; see
// buildCategoryOptions.

interface FormField {
  id: string;
  field_type: string;
  label: string;
  placeholder?: string;
  help_text?: string;
  is_required: boolean;
  /** `multiple` is worker_select only: absent or false = one person. The answer is string[] either way. */
  /** `prefill`: where the fill screen takes the starting value from (lib/safety-form-prefill). */
  options?: { choices?: FormChoice[]; layout?: 'inline'; multiple?: boolean; prefill?: PrefillSource };
  conditional_logic?: { show_if?: ShowIf } | null;
  sort_order: number;
}

/** A rule with no parent, or no values, would hide its field forever. */
function isIncompleteRule(f: FormField): boolean {
  const showIf = f.conditional_logic?.show_if;
  if (!showIf) return false;
  return !showIf.field_id || ruleValues(showIf).length === 0;
}

/** Ids of every field whose rule chain leads back to `fieldId` — it cannot be their parent's child. */
function descendantIds(fieldId: string, fields: FormField[]): Set<string> {
  const out = new Set<string>();
  const walk = (id: string) => {
    for (const f of fields) {
      if (f.conditional_logic?.show_if?.field_id === id && !out.has(f.id)) {
        out.add(f.id);
        walk(f.id);
      }
    }
  };
  walk(fieldId);
  return out;
}

function FieldTypeIcon({ type }: { type: string }) {
  const ft = FIELD_TYPES.find(f => f.value === type);
  if (!ft) return <Type size={16} />;
  const Icon = ft.icon;
  return <Icon size={16} />;
}

/* ── Live Preview ── */
function FormPreview({ name, fields }: { name: string; fields: FormField[] }) {
  return (
    <div className="bg-fieldiq-dark border border-fieldiq-border rounded-xl p-4 max-w-sm mx-auto">
      <h3 className="text-sm font-bold text-white mb-4 text-center">{name || 'Untitled Form'}</h3>
      <div className="space-y-3">
        {fields.map(f => (
          <div key={f.id}>
            {f.field_type === 'section_header' && (
              <div className="border-b border-fieldiq-border pb-1 mt-2">
                <p className="text-xs font-bold text-white">{f.label || 'Section'}</p>
              </div>
            )}
            {f.field_type === 'instructions' && (
              <p className="text-[11px] text-fieldiq-muted italic">{f.label || 'Instructions text'}</p>
            )}
            {(f.field_type === 'text' || f.field_type === 'number') && (
              <div>
                <label className="text-[11px] text-fieldiq-text mb-0.5 block">
                  {f.label}{f.is_required && <span className="text-red-400">*</span>}
                </label>
                <div className="bg-fieldiq-card border border-fieldiq-border rounded px-2 py-1.5 text-[11px] text-fieldiq-muted">
                  {f.placeholder || (f.field_type === 'number' ? '0' : 'Enter text...')}
                </div>
              </div>
            )}
            {f.field_type === 'textarea' && (
              <div>
                <label className="text-[11px] text-fieldiq-text mb-0.5 block">
                  {f.label}{f.is_required && <span className="text-red-400">*</span>}
                </label>
                <div className="bg-fieldiq-card border border-fieldiq-border rounded px-2 py-2 text-[11px] text-fieldiq-muted h-12">
                  {f.placeholder || 'Enter text...'}
                </div>
              </div>
            )}
            {f.field_type === 'yes_no' && (
              <div>
                <label className="text-[11px] text-fieldiq-text mb-1 block">
                  {f.label}{f.is_required && <span className="text-red-400">*</span>}
                </label>
                <div className="flex gap-1">
                  <div className="flex-1 text-center bg-fieldiq-card border border-fieldiq-border rounded px-2 py-1 text-[11px] text-fieldiq-text">Yes</div>
                  <div className="flex-1 text-center bg-fieldiq-card border border-fieldiq-border rounded px-2 py-1 text-[11px] text-fieldiq-text">No</div>
                </div>
              </div>
            )}
            {f.field_type === 'dropdown' && (
              <div>
                <label className="text-[11px] text-fieldiq-text mb-0.5 block">
                  {f.label}{f.is_required && <span className="text-red-400">*</span>}
                </label>
                <div className="bg-fieldiq-card border border-fieldiq-border rounded px-2 py-1.5 text-[11px] text-fieldiq-muted">
                  Select...
                </div>
              </div>
            )}
            {(f.field_type === 'checkbox_group' || f.field_type === 'radio') && (
              <div>
                <label className="text-[11px] text-fieldiq-text mb-0.5 block">
                  {f.label}{f.is_required && <span className="text-red-400">*</span>}
                </label>
                <div className={f.options?.layout === 'inline' ? 'flex flex-wrap gap-1' : 'space-y-1'}>
                  {normalizeChoices(f.options).map((c, ci) => (
                    <div key={ci} className="flex items-start gap-1.5 bg-fieldiq-card border border-fieldiq-border rounded px-2 py-1">
                      <span
                        aria-hidden
                        className={`mt-0.5 h-2.5 w-2.5 shrink-0 border border-fieldiq-muted ${f.field_type === 'radio' ? 'rounded-full' : 'rounded-[2px]'}`}
                      />
                      <span className="min-w-0">
                        <span className="block text-[11px] text-fieldiq-text">{c.value}</span>
                        {c.description && (
                          <span className="block text-[10px] text-fieldiq-muted">{c.description}</span>
                        )}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {f.conditional_logic?.show_if && (
              <p className="mt-0.5 flex items-center gap-1 text-[10px] text-fieldiq-muted">
                <GitBranch size={10} /> Shown only when a rule is met
              </p>
            )}
            {(f.field_type === 'date' || f.field_type === 'time') && (
              <div>
                <label className="text-[11px] text-fieldiq-text mb-0.5 block">
                  {f.label}{f.is_required && <span className="text-red-400">*</span>}
                </label>
                <div className="bg-fieldiq-card border border-fieldiq-border rounded px-2 py-1.5 text-[11px] text-fieldiq-muted">
                  {f.field_type === 'date' ? 'YYYY-MM-DD' : 'HH:MM'}
                </div>
              </div>
            )}
            {f.field_type === 'photo' && (
              <div>
                <label className="text-[11px] text-fieldiq-text mb-0.5 block">{f.label}</label>
                <div className="bg-fieldiq-card border border-dashed border-fieldiq-border rounded px-2 py-3 text-center">
                  <Camera size={14} className="mx-auto text-fieldiq-muted mb-1" />
                  <p className="text-[10px] text-fieldiq-muted">Tap to capture</p>
                </div>
              </div>
            )}
            {f.field_type === 'signature' && (
              <div>
                <label className="text-[11px] text-fieldiq-text mb-0.5 block">{f.label}</label>
                <div className="bg-white/90 rounded h-10 flex items-center justify-center">
                  <PenTool size={12} className="text-gray-400" />
                </div>
              </div>
            )}
            {f.field_type === 'gps_location' && (
              <div>
                <label className="text-[11px] text-fieldiq-text mb-0.5 block">{f.label}</label>
                <div className="bg-fieldiq-card border border-fieldiq-border rounded px-2 py-1.5 text-[11px] text-fieldiq-muted flex items-center gap-1">
                  <MapPin size={10} /> Auto-captured
                </div>
              </div>
            )}
            {f.field_type === 'worker_select' && (
              <div>
                <label className="text-[11px] text-fieldiq-text mb-0.5 block">
                  {f.label}{f.is_required && <span className="text-red-400">*</span>}
                </label>
                <div className="bg-fieldiq-card border border-fieldiq-border rounded px-2 py-1.5 text-[11px] text-fieldiq-muted">
                  {f.options?.multiple ? 'Select people...' : 'Select a person...'}
                </div>
              </div>
            )}
          </div>
        ))}
        {fields.length === 0 && (
          <p className="text-center text-[11px] text-fieldiq-muted py-6">Add fields to see preview</p>
        )}
      </div>
    </div>
  );
}

/* ── Options editor: value + optional description, and the inline toggle ── */
function ChoiceEditor({
  field,
  readOnly,
  onChange,
  onLayout,
}: {
  field: FormField;
  readOnly: boolean;
  onChange: (choices: NormalizedChoice[], rename?: { from: string; to: string | null }) => void;
  onLayout: (layout: 'inline' | null) => void;
}) {
  const choices = normalizeChoices(field.options);
  const describable = INLINE_LAYOUT_TYPES.has(field.field_type);
  const inline = field.options?.layout === 'inline';

  const setAt = (ci: number, patch: Partial<NormalizedChoice>) => {
    const next = choices.map((c, i) => (i === ci ? { ...c, ...patch } : c));
    const rename = patch.value !== undefined ? { from: choices[ci].value, to: patch.value } : undefined;
    onChange(next, rename);
  };

  return (
    <div>
      <label className="text-xs text-fieldiq-text mb-1 block">Options</label>
      <div className="space-y-2">
        {choices.map((choice, ci) => (
          <div key={ci} className="space-y-1">
            <div className="flex items-center gap-1.5">
              <input
                type="text"
                value={choice.value}
                aria-label={`Option ${ci + 1}`}
                onChange={e => setAt(ci, { value: e.target.value })}
                disabled={readOnly}
                className="flex-1 bg-fieldiq-card border border-fieldiq-border rounded px-2 py-1 text-white text-sm focus:outline-none focus:border-fieldiq-accent disabled:opacity-60"
              />
              {!readOnly && (
                <button
                  type="button"
                  aria-label={`Remove option ${ci + 1}`}
                  onClick={() => onChange(choices.filter((_, i) => i !== ci), { from: choice.value, to: null })}
                  className="text-fieldiq-muted hover:text-red-400 p-0.5"
                >
                  <Trash2 size={12} />
                </button>
              )}
            </div>
            {describable && (
              <input
                type="text"
                value={choice.description ?? ''}
                aria-label={`Option ${ci + 1} description`}
                placeholder="Description (optional)"
                onChange={e => setAt(ci, { description: e.target.value })}
                disabled={readOnly}
                className="w-full bg-fieldiq-card border border-fieldiq-border rounded px-2 py-1 text-xs text-fieldiq-text focus:outline-none focus:border-fieldiq-accent disabled:opacity-60"
              />
            )}
          </div>
        ))}
        {!readOnly && (
          <button
            type="button"
            onClick={() => onChange([...choices, { value: `Option ${choices.length + 1}`, description: null }])}
            className="text-xs text-fieldiq-accent hover:text-fieldiq-accent/80 flex items-center gap-1"
          >
            <Plus size={12} /> Add option
          </button>
        )}
      </div>
      {describable && (
        <label className="mt-2 flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={inline}
            onChange={e => onLayout(e.target.checked ? 'inline' : null)}
            disabled={readOnly}
            className="w-4 h-4 rounded"
          />
          <span className="text-xs text-fieldiq-text">Show options in a row (wraps on a phone)</span>
        </label>
      )}
    </div>
  );
}

/* ── "Show only when…" ── */
function RuleEditor({
  field,
  fields,
  readOnly,
  onChange,
}: {
  field: FormField;
  fields: FormField[];
  readOnly: boolean;
  onChange: (showIf: ShowIf | null) => void;
}) {
  const showIf = field.conditional_logic?.show_if ?? null;
  // A parent can be any choice or yes/no field other than this one and the
  // fields that already depend on it — picking one of those would make a
  // loop that can never be satisfied.
  const blocked = descendantIds(field.id, fields);
  const parents = fields.filter(f => f.id !== field.id && !blocked.has(f.id) && RULE_PARENT_TYPES.has(f.field_type));
  const parent = showIf ? fields.find(f => f.id === showIf.field_id) : undefined;
  const selected = ruleValues(showIf);
  const offered = parent ? ruleValuesFor(parent) : [];
  // Values the rule still names but the parent no longer offers.
  const stale = selected.filter(v => !offered.includes(v));

  const toggleValue = (v: string) => {
    if (!showIf) return;
    const next = selected.includes(v) ? selected.filter(x => x !== v) : [...selected, v];
    onChange({ field_id: showIf.field_id, any_of: next });
  };

  if (!showIf) {
    if (readOnly) return null;
    return (
      <div>
        <button
          type="button"
          onClick={() => onChange({ field_id: '', any_of: [] })}
          disabled={parents.length === 0}
          className="text-xs text-fieldiq-accent hover:text-fieldiq-accent/80 flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <GitBranch size={12} /> Show only when…
        </button>
        {parents.length === 0 && (
          <p className="mt-0.5 text-[11px] text-fieldiq-muted">
            A rule needs a radio, dropdown, checkbox group or yes/no field to depend on.
          </p>
        )}
      </div>
    );
  }

  return (
    <fieldset className="rounded-lg border border-fieldiq-border p-3 space-y-2" data-testid={`rule-editor-${field.id}`}>
      <legend className="px-1 text-xs font-semibold text-fieldiq-text flex items-center gap-1">
        <GitBranch size={12} aria-hidden /> Show only when…
      </legend>
      <select
        aria-label="Parent field"
        value={showIf.field_id}
        onChange={e => onChange({ field_id: e.target.value, any_of: [] })}
        disabled={readOnly}
        className="w-full bg-fieldiq-card border border-fieldiq-border rounded px-2.5 py-1.5 text-white text-sm focus:outline-none focus:border-fieldiq-accent disabled:opacity-60"
      >
        <option value="">Pick a field…</option>
        {parents.map(p => (
          <option key={p.id} value={p.id}>{p.label || 'Untitled field'}</option>
        ))}
        {showIf.field_id && !parent && <option value={showIf.field_id}>— field no longer on this form —</option>}
      </select>
      {parent && (
        <div>
          <p className="text-xs text-fieldiq-muted mb-1">
            {parent.field_type === 'checkbox_group' ? 'is ticked with any of:' : 'is any of:'}
          </p>
          <div className="flex flex-wrap gap-1.5">
            {offered.map(v => (
              <label
                key={v}
                className={`flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs cursor-pointer ${
                  selected.includes(v) ? 'border-fieldiq-accent bg-fieldiq-accent/15 text-fieldiq-text' : 'border-fieldiq-border text-fieldiq-text'
                }`}
              >
                <input
                  type="checkbox"
                  checked={selected.includes(v)}
                  onChange={() => toggleValue(v)}
                  disabled={readOnly}
                  className="w-3.5 h-3.5"
                />
                {v || <span className="italic text-fieldiq-muted">blank option</span>}
              </label>
            ))}
            {stale.map(v => (
              <button
                key={`stale-${v}`}
                type="button"
                onClick={() => toggleValue(v)}
                disabled={readOnly}
                className="flex items-center gap-1 rounded-full border border-dashed border-fieldiq-border px-2.5 py-1 text-xs text-fieldiq-muted"
                aria-label={`${v}: no longer an option on the parent field. Remove it from the rule`}
              >
                {v} (removed) <Trash2 size={10} />
              </button>
            ))}
          </div>
        </div>
      )}
      {selected.length === 0 && (
        <p className="text-xs text-fieldiq-text flex items-center gap-1">
          <AlertTriangle size={12} className="text-fieldiq-accent" aria-hidden />
          Until a {showIf.field_id ? 'value' : 'field and value'} is picked this field never shows. The form will not save like that.
        </p>
      )}
      {!readOnly && (
        <button
          type="button"
          onClick={() => onChange(null)}
          className="text-xs text-fieldiq-muted hover:text-red-400 flex items-center gap-1"
        >
          <Trash2 size={12} /> Remove rule (always show)
        </button>
      )}
    </fieldset>
  );
}

export default function SafetyFormBuilderScreen() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditing = !!id;
  /*
   * Save and Back go BACK one entry, to the template list the builder was
   * opened from. Both used to put a fresh copy of the list on the stack
   * (Back pushed it, save replaced this entry with it), so history read
   * list → builder → list and the list's Back reopened the builder.
   */
  const goBack = useBack('/settings/safety/forms');
  const queryClient = useQueryClient();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('other');
  const [requiresJobLink, setRequiresJobLink] = useState(false);
  const [requiresSignature, setRequiresSignature] = useState(false);
  const [allowPhotos, setAllowPhotos] = useState(true);
  const [allowGps, setAllowGps] = useState(false);
  const [fields, setFields] = useState<FormField[]>([]);
  const [expandedField, setExpandedField] = useState<string | null>(null);
  const [showFieldPicker, setShowFieldPicker] = useState(false);
  const [showPublishWarning, setShowPublishWarning] = useState(false);
  const [pendingPublish, setPendingPublish] = useState(false);

  // Load existing template
  const { data: template, isLoading: loadingTemplate } = useQuery({
    queryKey: ['safety-template', id],
    queryFn: () => api.safety.getTemplate(id!).then(r => r.data?.data ?? r.data),
    enabled: isEditing,
  });

  /**
   * The tenant's live categories, from the same endpoint the library page
   * groups by.
   *
   * The STRICT read on purpose — no `include_drafts`. That variant is
   * admin/dispatcher only and 403s for the office_staff who may legitimately
   * be in this builder, and it is not needed: a draft's own category is
   * guaranteed to be offered because `buildCategoryOptions` unions in whatever
   * value the select is currently holding.
   */
  const { data: liveCategories = [] } = useQuery({
    queryKey: ['forms-library-categories'],
    // The cache holds the TEMPLATES, which is what the extraction review screen
    // stores under this key; this screen's category list is derived by
    // `select`. Storing the derived strings here handed the review screen
    // strings where it reads templates, and the reverse threw in the
    // category label.
    queryFn: async () => {
      const res = await api.forms.templates();
      return unwrapArray<FormLibraryTemplate>(res.data);
    },
    select: listFormCategories,
  });

  const categoryOptions = useMemo(
    () => buildCategoryOptions(liveCategories, category),
    [liveCategories, category],
  );

  useEffect(() => {
    if (template) {
      setName(template.name || '');
      setDescription(template.description || '');
      setCategory(template.category || 'other');
      setRequiresJobLink(template.requires_job_link ?? false);
      setRequiresSignature(template.requires_signature ?? false);
      setAllowPhotos(template.allow_photos ?? true);
      setAllowGps(template.allow_gps ?? false);
      if (template.fields) {
        setFields(
          template.fields.map((f: any, i: number) => ({
            id: f.id || newFieldId(),
            field_type: f.field_type,
            label: f.label || '',
            placeholder: f.placeholder || '',
            help_text: f.help_text || '',
            is_required: f.is_required ?? false,
            options: f.options || {},
            conditional_logic: f.conditional_logic || null,
            sort_order: f.sort_order ?? i,
          }))
        );
      }
    }
  }, [template]);

  const isSystemTemplate = !!(template && (template.is_system || template.isSystem));
  const readOnly = isSystemTemplate;

  const buildPayload = (status: string) => ({
    name,
    description,
    category,
    status,
    requires_job_link: requiresJobLink,
    requires_signature: requiresSignature,
    allow_photos: allowPhotos,
    allow_gps: allowGps,
    fields: fields.map((f, i) => ({
      // ALWAYS sent. A new field's id is a client-minted UUID (newFieldId) and
      // the API keeps it, so a show_if written against a never-saved field
      // still names that field after the save. Dropping the id here (as this
      // line used to for new fields) gave every save fresh ids and orphaned
      // every rule pointing at them.
      id: f.id,
      field_type: f.field_type,
      label: f.label,
      placeholder: f.placeholder || null,
      help_text: f.help_text || null,
      is_required: f.is_required,
      options: f.options && Object.keys(f.options).length > 0 ? f.options : null,
      conditional_logic: f.conditional_logic || null,
      sort_order: i,
    })),
  });

  const saveMutation = useMutation({
    mutationFn: (payload: any) =>
      isEditing ? api.safety.updateTemplate(id!, payload) : api.safety.createTemplate(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['safety-templates'] });
      toast.success(isEditing ? 'Template updated' : 'Template created');
      goBack();
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.error || 'Failed to save template');
    },
  });

  /** Refuse to save a rule that would hide its field forever, and open the field that has one. */
  const rulesAreComplete = () => {
    const bad = fields.find(isIncompleteRule);
    if (!bad) return true;
    setExpandedField(bad.id);
    toast.error(`"${bad.label || 'Untitled field'}" has a show rule with no ${bad.conditional_logic?.show_if?.field_id ? 'values' : 'field'} picked`);
    return false;
  };

  const handleSaveDraft = () => {
    if (!rulesAreComplete()) return;
    saveMutation.mutate(buildPayload('draft'));
  };

  const handlePublish = () => {
    if (!rulesAreComplete()) return;
    // If editing an active template, warn about existing submissions
    if (isEditing && template?.status === 'active') {
      setShowPublishWarning(true);
    } else {
      saveMutation.mutate(buildPayload('active'));
    }
  };

  const confirmPublish = () => {
    setShowPublishWarning(false);
    saveMutation.mutate(buildPayload('active'));
  };

  const addField = (fieldType: string) => {
    const newField: FormField = {
      id: newFieldId(),
      field_type: fieldType,
      label: '',
      placeholder: '',
      help_text: '',
      is_required: false,
      options: CHOICE_FIELD_TYPES.has(fieldType)
        ? { choices: fieldType === 'radio' ? ['Option 1', 'Option 2'] : ['Option 1'] }
        : {},
      conditional_logic: null,
      sort_order: fields.length,
    };
    setFields([...fields, newField]);
    setExpandedField(newField.id);
    setShowFieldPicker(false);
  };

  const updateField = (id: string, updates: Partial<FormField>) => {
    setFields(prev => prev.map(f => f.id === id ? { ...f, ...updates } : f));
  };

  /**
   * Edit a choice field's options, carrying any rules that point at it along.
   * `rename` maps an old value to its new spelling (null = the option was
   * deleted), so a child's any_of follows a renamed option instead of going
   * stale and hiding the child forever.
   */
  const updateChoices = (
    id: string,
    choices: NormalizedChoice[],
    rename?: { from: string; to: string | null },
  ) => {
    setFields(prev => prev.map(f => {
      if (f.id === id) return { ...f, options: { ...f.options, choices: serializeChoices(choices) } };
      const showIf = f.conditional_logic?.show_if;
      if (!rename || !showIf || showIf.field_id !== id) return f;
      const values = ruleValues(showIf);
      if (!values.includes(rename.from)) return f;
      const next = values
        .map(v => (v === rename.from ? rename.to : v))
        .filter((v): v is string => v !== null);
      return { ...f, conditional_logic: { show_if: { field_id: id, any_of: Array.from(new Set(next)) } } };
    }));
  };

  const setRule = (id: string, showIf: ShowIf | null) => {
    updateField(id, { conditional_logic: showIf ? { show_if: showIf } : null });
  };

  const removeField = (id: string) => {
    const orphans = dependentsOf(id, fields);
    setFields(prev => prev
      .filter(f => f.id !== id)
      // A child whose parent is gone keeps no rule — it would never show.
      .map(f => (f.conditional_logic?.show_if?.field_id === id ? { ...f, conditional_logic: null } : f)));
    if (orphans.length > 0) {
      toast.info(`${orphans.length} field${orphans.length === 1 ? '' : 's'} depended on this one and will now always show`);
    }
    if (expandedField === id) setExpandedField(null);
  };

  const moveField = (index: number, dir: -1 | 1) => {
    const newIndex = index + dir;
    if (newIndex < 0 || newIndex >= fields.length) return;
    const newFields = [...fields];
    [newFields[index], newFields[newIndex]] = [newFields[newIndex], newFields[index]];
    setFields(newFields);
  };

  if (isEditing && loadingTemplate) {
    return (
      <div className="min-h-full bg-background flex items-center justify-center">
        <Loader2 size={24} className="animate-spin text-fieldiq-accent" />
      </div>
    );
  }

  return (
    <div className="min-h-full pb-[var(--bottom-chrome)] bg-background">
      <div className="border-b border-border px-6 py-4">
        <button
          onClick={goBack}
          className="flex items-center gap-2 text-white hover:text-amber-400 hover:bg-white/10 text-lg font-semibold px-2 py-1 rounded transition-colors"
        >
          <ArrowLeft className="w-6 h-6" />
          Back
        </button>
        <div className="flex items-center justify-between mt-2">
          <h1 className="text-base font-bold text-foreground">
            {readOnly ? 'Preview System Template' : isEditing ? 'Edit Form Template' : 'Create Form Template'}
          </h1>
          <div className="flex items-center gap-2">
            {readOnly ? (
              <button
                onClick={() => {
                  const cloneReq = api.safety.cloneTemplate(id!);
                  cloneReq.then((res: any) => {
                    queryClient.invalidateQueries({ queryKey: ['safety-templates'] });
                    const cloned = res.data?.data ?? res.data;
                    if (cloned?.id) {
                      toast.success('Template cloned — opening editor');
                      // Replace the read-only preview: Back from the copy is the list.
                      navigate(`/settings/safety/forms/${cloned.id}/edit`, { replace: true });
                    }
                  }).catch(() => toast.error('Failed to clone template'));
                }}
                className="flex items-center gap-2 px-4 py-2 bg-fieldiq-accent text-white rounded-lg text-sm font-medium hover:bg-fieldiq-accent/90"
              >
                <Copy size={16} />
                Clone to Customize
              </button>
            ) : (
              <>
                <button
                  onClick={handleSaveDraft}
                  disabled={saveMutation.isPending || !name.trim()}
                  className="px-4 py-2 bg-fieldiq-border text-white rounded-lg text-sm font-medium hover:bg-fieldiq-border/80 disabled:opacity-50"
                >
                  Save as Draft
                </button>
                <button
                  onClick={handlePublish}
                  disabled={saveMutation.isPending || !name.trim() || fields.length === 0}
                  className="px-4 py-2 bg-fieldiq-accent text-white rounded-lg text-sm font-medium hover:bg-fieldiq-accent/90 disabled:opacity-50"
                >
                  {saveMutation.isPending ? 'Saving...' : 'Publish'}
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="px-6 py-4">
        <div className="flex gap-6 flex-col lg:flex-row">
          {/* Left column — settings + fields */}
          <div className="flex-1 space-y-4 min-w-0">
            {/* Form settings */}
            <div className="bg-fieldiq-card border border-fieldiq-border rounded-xl p-4 space-y-4">
              <h3 className="font-semibold text-white text-sm">Form Settings</h3>
              <div>
                <label className="text-xs text-fieldiq-text mb-1 block">Form Name *</label>
                <input
                  type="text"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder="e.g. Daily Safety Checklist"
                  disabled={readOnly}
                  className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-fieldiq-accent disabled:opacity-60"
                />
              </div>
              <div>
                <label className="text-xs text-fieldiq-text mb-1 block">Description</label>
                <textarea
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  placeholder="Brief description of this form..."
                  rows={2}
                  disabled={readOnly}
                  className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-fieldiq-accent resize-none disabled:opacity-60"
                />
              </div>
              <div>
                <label className="text-xs text-fieldiq-text mb-1 block">Category</label>
                <select
                  value={category}
                  onChange={e => setCategory(e.target.value)}
                  disabled={readOnly}
                  className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-fieldiq-accent disabled:opacity-60"
                >
                  {categoryOptions.map(c => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { label: 'Requires Job Link', value: requiresJobLink, set: setRequiresJobLink },
                  { label: 'Requires Signature', value: requiresSignature, set: setRequiresSignature },
                  { label: 'Allow Photos', value: allowPhotos, set: setAllowPhotos },
                  { label: 'Allow GPS', value: allowGps, set: setAllowGps },
                ].map(toggle => (
                  <label key={toggle.label} className="flex items-center gap-2 cursor-pointer">
                    <button
                      type="button"
                      onClick={() => !readOnly && toggle.set(!toggle.value)}
                      disabled={readOnly}
                      className={`relative inline-flex h-6 w-10 items-center rounded-full transition-colors disabled:opacity-60 ${
                        toggle.value ? 'bg-fieldiq-accent' : 'bg-fieldiq-border'
                      }`}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        toggle.value ? 'translate-x-5' : 'translate-x-1'
                      }`} />
                    </button>
                    <span className="text-xs text-fieldiq-text">{toggle.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Fields list */}
            <div className="bg-fieldiq-card border border-fieldiq-border rounded-xl p-4">
              <h3 className="font-semibold text-white text-sm mb-3">Form Fields ({fields.length})</h3>
              <div className="space-y-2">
                {fields.map((field, index) => (
                  <div
                    key={field.id}
                    className="bg-fieldiq-dark border border-fieldiq-border rounded-lg overflow-hidden"
                  >
                    {/* Field header row */}
                    <div
                      className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-fieldiq-border/30"
                      onClick={() => setExpandedField(expandedField === field.id ? null : field.id)}
                    >
                      <GripVertical size={14} className="text-fieldiq-muted flex-shrink-0" />
                      <FieldTypeIcon type={field.field_type} />
                      <span className="text-sm text-white flex-1 truncate">
                        {field.label || <span className="text-fieldiq-muted italic">Untitled field</span>}
                      </span>
                      {field.is_required && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-500/20 text-red-400 font-semibold">Required</span>
                      )}
                      {field.conditional_logic?.show_if && (
                        <span
                          data-testid={`conditional-badge-${field.id}`}
                          className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-fieldiq-accent/15 text-fieldiq-text font-semibold"
                        >
                          <GitBranch size={10} aria-hidden /> Conditional
                        </span>
                      )}
                      <span className="text-[10px] text-fieldiq-muted">{field.field_type.replace(/_/g, ' ')}</span>
                      {!readOnly && (
                        <div className="flex items-center gap-0.5">
                          <button aria-label={`Move ${field.label || 'field'} up`} onClick={e => { e.stopPropagation(); moveField(index, -1); }} disabled={index === 0}
                            className="p-0.5 text-fieldiq-muted hover:text-white disabled:opacity-30"><ChevronUp size={14} /></button>
                          <button aria-label={`Move ${field.label || 'field'} down`} onClick={e => { e.stopPropagation(); moveField(index, 1); }} disabled={index === fields.length - 1}
                            className="p-0.5 text-fieldiq-muted hover:text-white disabled:opacity-30"><ChevronDown size={14} /></button>
                          <button aria-label={`Delete ${field.label || 'field'}`} onClick={e => { e.stopPropagation(); removeField(field.id); }}
                            className="p-0.5 text-fieldiq-muted hover:text-red-400"><Trash2 size={14} /></button>
                        </div>
                      )}
                    </div>

                    {/* Expanded inline editor */}
                    {expandedField === field.id && (
                      <div className="border-t border-fieldiq-border px-3 py-3 space-y-3">
                        <div>
                          <label className="text-xs text-fieldiq-text mb-1 block">Label</label>
                          <input
                            type="text"
                            value={field.label}
                            onChange={e => updateField(field.id, { label: e.target.value })}
                            placeholder="Field label"
                            disabled={readOnly}
                            className="w-full bg-fieldiq-card border border-fieldiq-border rounded px-2.5 py-1.5 text-white text-sm focus:outline-none focus:border-fieldiq-accent disabled:opacity-60"
                          />
                        </div>
                        {!['section_header', 'instructions', 'photo', 'signature', 'gps_location'].includes(field.field_type) && (
                          <div>
                            <label className="text-xs text-fieldiq-text mb-1 block">Placeholder</label>
                            <input
                              type="text"
                              value={field.placeholder || ''}
                              onChange={e => updateField(field.id, { placeholder: e.target.value })}
                              disabled={readOnly}
                              className="w-full bg-fieldiq-card border border-fieldiq-border rounded px-2.5 py-1.5 text-white text-sm focus:outline-none focus:border-fieldiq-accent disabled:opacity-60"
                            />
                          </div>
                        )}
                        <div>
                          <label className="text-xs text-fieldiq-text mb-1 block">Help Text</label>
                          <input
                            type="text"
                            value={field.help_text || ''}
                            onChange={e => updateField(field.id, { help_text: e.target.value })}
                            disabled={readOnly}
                            className="w-full bg-fieldiq-card border border-fieldiq-border rounded px-2.5 py-1.5 text-white text-sm focus:outline-none focus:border-fieldiq-accent disabled:opacity-60"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-fieldiq-text mb-1 block">Field Type</label>
                          <select
                            value={field.field_type}
                            onChange={e => {
                              const next = e.target.value;
                              const seed = CHOICE_FIELD_TYPES.has(next) && normalizeChoices(field.options).length === 0;
                              updateField(field.id, {
                                field_type: next,
                                ...(seed ? { options: { ...field.options, choices: ['Option 1'] } } : {}),
                              });
                            }}
                            disabled={readOnly}
                            className="w-full bg-fieldiq-card border border-fieldiq-border rounded px-2.5 py-1.5 text-white text-sm focus:outline-none focus:border-fieldiq-accent disabled:opacity-60"
                          >
                            {FIELD_TYPES.map(ft => (
                              <option key={ft.value} value={ft.value}>{ft.label}</option>
                            ))}
                          </select>
                        </div>
                        {!['section_header', 'instructions'].includes(field.field_type) && (
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={field.is_required}
                              onChange={e => updateField(field.id, { is_required: e.target.checked })}
                              className="w-4 h-4 rounded"
                            />
                            <span className="text-xs text-fieldiq-text">Required</span>
                          </label>
                        )}
                        {field.field_type === 'worker_select' && (
                          <div>
                            <label htmlFor={`people-${field.id}`} className="text-xs text-fieldiq-text mb-1 block">People</label>
                            <select
                              id={`people-${field.id}`}
                              value={field.options?.multiple ? 'multiple' : 'single'}
                              onChange={e => updateField(field.id, {
                                options: { ...field.options, multiple: e.target.value === 'multiple' },
                              })}
                              disabled={readOnly}
                              className="w-full bg-fieldiq-card border border-fieldiq-border rounded px-2.5 py-1.5 text-white text-sm focus:outline-none focus:border-fieldiq-accent disabled:opacity-60"
                            >
                              <option value="single">Single person</option>
                              <option value="multiple">Multiple people</option>
                            </select>
                          </div>
                        )}
                        {prefillSourcesFor(field.field_type).length > 0 && (
                          <div>
                            <label htmlFor={`prefill-${field.id}`} className="text-xs text-fieldiq-text mb-1 block">Fill from</label>
                            <select
                              id={`prefill-${field.id}`}
                              value={fieldPrefill(field) ?? ''}
                              onChange={e => updateField(field.id, {
                                options: { ...field.options, prefill: (e.target.value || undefined) as PrefillSource | undefined },
                              })}
                              disabled={readOnly}
                              className="w-full bg-fieldiq-card border border-fieldiq-border rounded px-2.5 py-1.5 text-white text-sm focus:outline-none focus:border-fieldiq-accent disabled:opacity-60"
                            >
                              <option value="">None</option>
                              {prefillSourcesFor(field.field_type).map(src => (
                                <option key={src} value={src}>{PREFILL_LABELS[src]}</option>
                              ))}
                            </select>
                            <p className="mt-1 text-xs text-fieldiq-muted">Filled in when the form opens or a job is linked. Anything typed by hand is kept.</p>
                          </div>
                        )}
                        {CHOICE_FIELD_TYPES.has(field.field_type) && (
                          <ChoiceEditor
                            field={field}
                            readOnly={readOnly}
                            onChange={(choices, rename) => updateChoices(field.id, choices, rename)}
                            onLayout={layout => updateField(field.id, {
                              options: { ...field.options, layout: layout ?? undefined },
                            })}
                          />
                        )}
                        <RuleEditor
                          field={field}
                          fields={fields}
                          readOnly={readOnly}
                          onChange={showIf => setRule(field.id, showIf)}
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Add field button */}
              {!readOnly && (
                <button
                  onClick={() => setShowFieldPicker(true)}
                  className="mt-3 w-full flex items-center justify-center gap-2 py-2.5 border-2 border-dashed border-fieldiq-border rounded-lg text-sm text-fieldiq-text hover:text-fieldiq-accent hover:border-fieldiq-accent transition-colors"
                >
                  <Plus size={16} />
                  Add Field
                </button>
              )}
            </div>
          </div>

          {/* Right column — live preview */}
          <div className="w-full lg:w-80 shrink-0">
            <div className="lg:sticky lg:top-4">
              <h3 className="font-semibold text-white text-sm mb-3">Live Preview</h3>
              <FormPreview name={name} fields={fields} />
            </div>
          </div>
        </div>
      </div>

      {/* Field type picker dialog */}
      <Dialog open={showFieldPicker} onOpenChange={setShowFieldPicker}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Field</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-2">
            {FIELD_TYPES.map(ft => {
              const Icon = ft.icon;
              return (
                <button
                  key={ft.value}
                  onClick={() => addField(ft.value)}
                  className="flex items-center gap-2 px-3 py-2.5 bg-fieldiq-dark border border-fieldiq-border rounded-lg text-sm text-white hover:border-fieldiq-accent hover:text-fieldiq-accent transition-colors"
                >
                  <Icon size={16} />
                  {ft.label}
                </button>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>

      {/* Publish warning dialog */}
      <Dialog open={showPublishWarning} onOpenChange={setShowPublishWarning}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle size={20} className="text-yellow-400" />
              Publish Changes
            </DialogTitle>
          </DialogHeader>
          <p className="text-fieldiq-text text-sm">
            This template is already active and may have existing submissions. Publishing changes will update the template for all future submissions. Existing submissions will not be affected.
          </p>
          <DialogFooter>
            <button
              onClick={() => setShowPublishWarning(false)}
              className="px-4 py-2 bg-fieldiq-border text-white rounded-lg text-sm font-medium"
            >
              Cancel
            </button>
            <button
              onClick={confirmPublish}
              className="px-4 py-2 bg-fieldiq-accent text-white rounded-lg text-sm font-medium hover:bg-fieldiq-accent/90"
            >
              Publish Changes
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
